import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature, Plus } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Spinner,
  TBody,
  TD,
  TDNum,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import { api } from '../lib/api';

export function TermsPage() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const termsQuery = useQuery({
    queryKey: ['terms'],
    queryFn: () => api.listTerms(),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">M2.8</p>
          <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
            Termos de Uso
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Versões dos Termos de Uso. Cada nova publicação é imutável e dispara aceite obrigatório
            no próximo login dos traders.
          </p>
        </div>
        <Button leadingIcon={<Plus size={14} />} onClick={() => setCreating(true)}>
          Publicar nova versão
        </Button>
      </div>

      <Card className="overflow-hidden">
        {termsQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size={24} />
          </div>
        ) : termsQuery.data && termsQuery.data.length > 0 ? (
          <Table>
            <THead>
              <TR>
                <TH>Versão</TH>
                <TH>Market</TH>
                <TH>Publicado em</TH>
                <TH className="text-right">Aceites</TH>
              </TR>
            </THead>
            <TBody>
              {termsQuery.data.map((t) => (
                <TR key={t.id}>
                  <TD>
                    <span className="text-brand-700 font-mono font-semibold">{t.version}</span>
                  </TD>
                  <TD>
                    {t.marketCode ? (
                      <Badge tone={t.marketCode === 'B3' ? 'brand' : 'warning'}>
                        {t.marketCode}
                      </Badge>
                    ) : (
                      <span className="text-ink-400 text-xs">global</span>
                    )}
                  </TD>
                  <TD className="text-ink-600 text-xs">
                    {new Date(t.publishedAt).toLocaleString('pt-BR')}
                  </TD>
                  <TDNum>{t.acceptanceCount}</TDNum>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <EmptyState
            icon={<FileSignature size={32} />}
            title="Nenhuma versão publicada"
            description="Publique a primeira versão dos Termos de Uso."
            action={<Button onClick={() => setCreating(true)}>Publicar agora</Button>}
          />
        )}
      </Card>

      {creating && (
        <PublishDrawer
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['terms'] });
          }}
        />
      )}
    </div>
  );
}

function PublishDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [version, setVersion] = useState('');
  const [marketCode, setMarketCode] = useState<'' | 'B3' | 'FOREX'>('');
  const [contentMd, setContentMd] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.publishTerms({
        version,
        ...(marketCode && { marketCode }),
        contentMd,
      }),
    onSuccess: () => onSaved(),
    onError: (err) => {
      if (err instanceof PnlApiError) {
        setError(
          err.api.fields ? Object.values(err.api.fields).flat().join(' · ') : err.api.message,
        );
      } else {
        setError('Erro de conexão.');
      }
    },
  });

  return (
    <div
      className="bg-ink-900/40 animate-fade-in fixed inset-0 z-50 flex justify-end backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="shadow-lift h-full w-full max-w-2xl overflow-y-auto bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-ink-200 border-b px-6 py-4">
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            Nova versão
          </p>
          <h2 className="font-display text-ink-900 mt-1 text-xl font-semibold">
            Publicar Termos de Uso
          </h2>
          <p className="text-warning-600 mt-1 text-xs">
            Atenção: versão imutável. Use markdown padrão.
          </p>
        </div>

        <form
          className="space-y-4 p-6"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate();
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Versão" required hint="Ex: v1.2.0 ou 2026-04-29">
              <Input
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                required
                pattern="[\w.-]+"
              />
            </Field>

            <Field label="Aplica-se a" hint="Deixe global para B3 e Forex">
              <select
                value={marketCode}
                onChange={(e) => setMarketCode(e.target.value as '' | 'B3' | 'FOREX')}
                className="form-select border-ink-200 text-ink-900 shadow-soft focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4"
              >
                <option value="">Global (B3 + Forex)</option>
                <option value="B3">Apenas B3</option>
                <option value="FOREX">Apenas Forex</option>
              </select>
            </Field>
          </div>

          <Field label="Conteúdo (markdown)" required hint="Mínimo 50 caracteres.">
            <textarea
              value={contentMd}
              onChange={(e) => setContentMd(e.target.value)}
              required
              minLength={50}
              rows={18}
              className="border-ink-200 text-ink-900 shadow-soft focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border bg-white px-3 py-2.5 font-mono text-xs focus:ring-4"
              placeholder="# Termos de Uso&#10;&#10;## 1. Objeto&#10;&#10;..."
            />
          </Field>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Publicar versão
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
